import React, { useContext } from 'react';
import { getUpdatedBasket } from 'pages/common/helpers/get-updated-basket';
import { complimentaryProductsState, useCalculationState } from 'pages/calculation/store/calculation';
import { DataContext } from 'entries/common/widgets/context';
import { gaAddToCart } from 'ga-events/ga-add-to-cart';
import { gaClickOnGoods } from 'ga-events/ga-click-goods';
import { dimensionsAtom } from 'pages/dimensions/store/dimensions';
import { useRecoilValue } from 'recoil';
import { getComplimentaryProductInfoForBasket } from 'pages/calculation/helpers/get-complimentary-product-info-for-basket.helper';
import { addComplimentaryProductsToBasketArr } from 'pages/common/helpers/add-complimentary-products-to-basket-arr';
import { getAllProductsInBasket } from 'pages/calculation/helpers/get-all-products-in-basket.helper';
import { updateTintServiceInServiceBasket } from 'pages/calculation/helpers/update-tint-service-in-service-basket.helper';
import { tintServiceIsAnyColorsSelected, tintServiceState } from 'pages/calculation/store/tint-service';
import { TTintColor } from 'pages/calculation/types/tint-color.type';
import { useBasketState } from 'pages/common/store/basket';
import { Cart } from 'pages/common/components/Cart';
import { BASKET_URL } from 'pages/common/consts/basket';
import { TBasketItem } from 'pages/common/types';
import { sendBasketSnackbar } from 'pages/common/helpers/send-basket-snackbar';
import { getProductQuantityInBasket } from 'pages/common/helpers/get-product-quantity-in-basket';

type TCartComponentProps = {
  productId: string;
  count: number;
};

export const CartComponent: React.FC<TCartComponentProps> = ({ productId, count }) => {
  const data = useContext(DataContext);
  const { calculationFormState, setCalculationState } = useCalculationState();
  const { addToCartIsSuccessful } = calculationFormState;
  const isAnyColorsSelected = useRecoilValue(tintServiceIsAnyColorsSelected);
  const tintState = useRecoilValue(tintServiceState);
  const { basket, addProductsToBasket } = useBasketState();

  const productQuantityInBasket = getProductQuantityInBasket(basket, productId);
  const dimensions = useRecoilValue(dimensionsAtom);
  const complimentaryProducts = useRecoilValue(complimentaryProductsState);
  const complimentaryProductInfoForBasket = getComplimentaryProductInfoForBasket({
    complimentaryProducts,
    checkedProducts: dimensions.checkedProducts,
  });

  const basketArr: TBasketItem[] = getUpdatedBasket(basket, {
    code: productId,
    quantity: productQuantityInBasket + count,
  });

  const complementaryProductsWithBasketProducts = addComplimentaryProductsToBasketArr({
    basketArr,
    complimentaryProductInfoForBasket,
  });

  const handleClick = async () => {
    const allProductsInBasket = getAllProductsInBasket({ dimensions, data, count, complimentaryProducts });
    document.body.addEventListener(
      'basket:update',
      e =>
        sendBasketSnackbar({
          e: e as CustomEvent,
          countMainProduct: count,
          countMainProductInBasket: productQuantityInBasket,
          mainProductId: productId,
        }),
      { once: true }
    );
    addProductsToBasket(complementaryProductsWithBasketProducts);
    allProductsInBasket.forEach(item =>
      gaAddToCart({
        id: item.productId,
        quantity: item.productQuantity,
        price: item.price ?? 0,
        name: item.name ?? '',
      })
    );
    setCalculationState(prevState => ({
      ...prevState,
      addToCartIsSuccessful: true,
    }));
    if (isAnyColorsSelected) {
      const { COLOR_NAME: color, HEX: hex } = tintState.selectedColor as TTintColor;

      updateTintServiceInServiceBasket({
        payload: {
          parentLineId: productId,
          color,
          hex,
          flowQty: count,
          serviceIdForTint: data?.serviceId as string,
        },
      });
    }
  };

  const onClickBasket = () => {
    gaClickOnGoods({
      title: 'Добавлено в корзину',
      name: 'Перейти в корзину',
    });

    window.open(`${window?.location?.origin}/${BASKET_URL}/`, '_blank', 'noopener noreferrer');
  };

  return (
    <Cart
      onClickBasket={onClickBasket}
      onClickToBasket={handleClick}
      addToCartIsSuccessful={addToCartIsSuccessful}
      formName="calculation-form"
      cartTitle="Корзина"
      toCartTitle="В корзину"
    />
  );
};
